import type { DocNode } from '@atlaskit/adf-schema/doc';

import type { ChatEntryPoint } from './chat-entry-point';
import type { SolutionDraftAgentUpdatePayload } from './common/types/agent';
import type { JsmJourneyBuilderActionsPayload } from './common/types/jsm-journey-builder';
import type {
	SolutionArchitectAgentActivationFlowStoppedPayload,
	SolutionArchitectAgentActivationPayload,
	SolutionArchitectAgentActivationFlowStartedPayload,
	SolutionArchitectHandoffPayload,
	SolutionPlanStateUpdatePayload,
	StudioAutomationBuildUpdatePayload,
	StudioLandingPageRedirectPayload,
	UpdateAgentConfigurationPayload,
} from './common/types/solution-architect';
import type { ChatContextPayload } from './common/utils/chat-context/types';
import type { RovoChatOpenMode } from './common/utils/params/types';

export const Topics = {
	AI_MATE: 'ai-mate',
	AI_MATE_ACTIONS: 'ai-mate-actions',
	AI_MATE_INSERT_URLS: 'ai-mate-chat-inserts',
	AI_MATE_CHAT_INPUT_OVERLAY: 'ai-mate-chat-input-overlay',
	AI_MATE_AIFC: 'ai-mate-aifc',
	ROVO_REMIX_LAUNCH: 'rovo-remix-launch',
	ROVO_REMIX: 'rovo-remix',
	ROVO_SKILLS: 'rovo-skills',
	AVP: 'avp',
} as const;
export type Topic = (typeof Topics)[keyof typeof Topics];

export type PayloadCore<TKey extends string, TData = void> = {
	type: TKey;
	source: string;
	openChat?: boolean;
	openChatMode?: RovoChatOpenMode;
	/** Open the mini-modal at its maximum size; ignored by sidebar opens. */
	openAtMaxSize?: boolean;
	autoFocusInput?: boolean;
	product?: string;
	interactionSource?: string;
	/**
	 * When true, subscribers that opt into the same consumeOnceKey should process this
	 * logical event once total. The delivery id used for deduplication is internal to
	 * rovo-triggers and is generated for each publish call.
	 */
	consumeOnce?: boolean;
} & (TData extends void ? {} : { data: TData });

export type MessageSendPayload = PayloadCore<
	'message-send',
	{
		prompt: string;
		productKey?: string;
		minionAlias?: string;
		requestedSkills?: string[];
		files?: UploadedFile[];
	}
>;

export type ChatClosePayload = PayloadCore<'chat-close', {}>;

export type SmartCreationModalOpenPayload = PayloadCore<
	'open-smart-creation-modal',
	{
		channelId?: string;
		entryPoint: string;
		stagingAreaActive: boolean;
		isFloating: boolean;
		shouldShowBackButton: boolean;
	}
>;

// Can only specify either `agentId` or `agentExternalConfigReference`, not both
type TargetAgentParam =
	| {
			agentId: string;
			agentExternalConfigReference?: never;
	  }
	| {
			agentId?: never;
			agentExternalConfigReference: string;
	  };

type PlaceholderParam = {
	// Overrides the default placeholder type
	placeholderType?: 'person' | 'link' | 'generic' | 'skill';
};

/**
 * Source-selection behavior requested when opening a chat.
 *
 * This public package owns the transport type so consumers do not depend on an internal package.
 * A test keeps it aligned with the assistance service contract.
 */
export type RovoChatSourceMode = 'GENERAL' | 'STRICT';

type ChatModeParam = {
	autoActionApprovalMode?: 'ALWAYS_HUMAN' | 'SHADOW' | 'AUTO_APPROVE';
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
	webSearchEnabled?: boolean;
	sourceMode?: RovoChatSourceMode;
	useCurrentPageContext?: boolean;
	spaceSourceCount?: number;
	appFilters?: unknown[];
};

export type ResultAttributionContextPayload = {
	/**
	 * Source surface that launched the agent chat, appended to generated result analytics.
	 */
	launchSource?: string;
};

type SerializableCreationContextValue =
	| string
	| number
	| boolean
	| null
	| undefined
	| { [key: string | number]: SerializableCreationContextValue }
	| SerializableCreationContextValue[];

/** Creation metadata forwarded when an editor surface launches Rovo Chat. */
export type ChatCreationContextParams = {
	templateInput?: {
		templateId: string;
		templateType: string;
	};
	jiraContext?: unknown;
	dynamicUiType?: string;
	dynamicUiSubtype?: string;
	dynamicUiSource?: unknown;
	forcedContentType?: string;
	contentMauiId?: string;
	mediaFileId?: string;
	source?: string;
	shouldUseExistingContent?: boolean;
	isViewMode?: boolean;
	experience?:
		| 'cwr'
		| 'cwr_type'
		| 'cwr_edit'
		| 'cwr_existing'
		| 'cwr_loom'
		| 'cwr-dev-docs'
		| 'inline_edit'
		| 'remix'
		| 'remix_edit'
		| 'remix_object'
		| 'remix_custom'
		| 'remix_custom_edit'
		| 'chat_edit'
		| 'chat_view'
		| 'keep_existing_page_structure';
	contentTypes?: string[];
	blocks?: string[];
	spaceKey?: string;
	additionalContext?: Record<string, SerializableCreationContextValue>;
};

export type ChatNewPayload = PayloadCore<
	'chat-new',
	{
		name?: string;
		// Used to prefab the chat history
		dialogues: Array<{
			human_message: {
				content: string;
				mimeType?: 'text/markdown' | 'text/adf';
			};
			agent_message: {
				content: string;
				mimeType?: 'text/markdown' | 'text/adf';
			};
		}>;
		// Used for follow-up prompt once chat is created
		prompt?: string | DocNode;
		/** Content targeted by the prompt when an editor surface launches chat. */
		contentId?: string | number;
		/** Creation context supplied by the launching editor surface. */
		creationContextParams?: ChatCreationContextParams;
		/**
		 * Overrides the default auto-send behavior for prompts.
		 * Set this to true to insert prompts not containing backticks into the chat input for dynamic
		 * user completion, rather than sending them immediately.
		 */
		overrideAutoSend?: boolean;
		// Used to indicate if the prompt is a placeholder, only works if `prompt` is a string
		isPromptPlaceholder?: boolean;
		files?: UploadedFile[];
		contentContext?: 'staging-area' | 'global';
		sourceId?: string;
		/** Correlates a Quick Find journey with the Chat activity it launches. */
		rovoJourneyId?: string;
		minionAlias?: string;
		requestedSkills?: string[];
		// Skip creating a seeded conversation in the BE with auto-generated name
		skipCreatingSeededConversation?: boolean;
		/**
		 * Opens the Rovo shell with loading while the seeded conversation is created, then selects
		 * that conversation once ready. Has no effect when `skipCreatingSeededConversation` is true.
		 */
		deferOpenUntilSeeded?: boolean;
		// Space to set as the selected space bar in the chat input
		selectedSpace?: { id: string; name: string; emoji?: string };
		// Reset to default chat view from previously selected view, e.g. Browse agents
		resetActiveMenu?: boolean;
		// Chat mode options to configure the conversation behavior
		mode?: ChatModeParam;
		// AI feature context to set in the store when the chat is created.
		// Each key-value pair is set via setAIFeatureContext. Stale entries for
		// known keys (e.g. 'projectContext') are cleared before new values are applied.
		aiFeatureContext?: Record<string, unknown>;
		/**
		 * Optional static analytics attribution for result events generated from this
		 * launched conversation.
		 */
		resultAttributionContext?: ResultAttributionContextPayload;
		// Agent version info for testing a specific version of an agent in the chat.
		// Used by Studio to pass the currently selected version when clicking Test.
		agentVersion?: {
			versionId?: string;
			versionType?: string;
			versionNumber?: number;
		};
		spaceId?: string;
		/**
		 * Optional artifact representing the object the user is currently interacting with or
		 * acting on (e.g. 3P forge artifacts that are not resolvable without an ARI).
		 * Sent as `search_artifact` in `body.context`.
		 */
		searchArtifact?: SendMessageSearchArtifact;
		/**
		 * Optional conversation-channel tags forwarded to the backend on conversation creation.
		 * Used by the agent-mention-in-comment feature to pass mention-in-comment, page:<pageId>,
		 * and comment:<commentId> so the backend can create a SessionAssociationPublic record.
		 */
		tags?: string[];
		/** Current Confluence comment invocation context for thread-level session reuse. */
		commentMentionContext?: {
			pageAri: string;
			commentAri: string;
			commentType: 'INLINE' | 'FOOTER';
		};
		/**
		 * Optimistic agent metadata for the editor agent mention flow.
		 * Allows showing the agent avatar telepointer immediately during AI streaming
		 * without an extra network round-trip to fetch agent details.
		 */
		agentName?: string;
		agentIdentityAccountId?: string;
		/** Identifies the product surface that launched the conversation for analytics attribution. */
		chatEntryPoint?: ChatEntryPoint;
		/**
		 * The `localId` of the agent-mention node that invoked this run. Threaded through so the
		 * agent-run-state bridge can key live run-state back to the node.
		 */
		invokedByNodeLocalId?: string;
	} & Partial<TargetAgentParam> &
		PlaceholderParam
>;

export type RovoSpaceEntryPoint =
	| 'leftNav'
	| 'sidebar'
	| 'zeroQuery'
	| 'typeahead'
	| 'inChatRecommendation';

export type EditorContextPayloadData =
	| {
			document: {
				type: 'text/markdown' | 'text/adf';
				content: string;
			};
			selection: {
				type: 'text/markdown' | 'text/plain';
				content: string;
			};
			selectionFragment?: string;
			selectionLocalIds?: string;
			contentMauiId?: string;
			mediaFileId?: string;
			mediaCollection?: string;
			dynamicUiSource?: {
				contentType: string;
				startLocalId?: string;
				endLocalId?: string;
				startIndex?: number;
				endIndex?: number;
				fragmentAdf?: string;
			};
			dynamicUiType?: string;
			dynamicUiSubtype?: string;
			isViewMode?: boolean;
			isDraftLockedForEditing?: boolean;
			useGenericEditorSkill?: boolean;
			additionalContext?: Record<string, unknown>;
	  }
	| undefined;

export type WhiteboardContextPayloadData =
	| {
			type: 'image/svg+xml' | 'text/plain';
			content: string;
			contentId?: string;
			isViewMode?: boolean;
	  }
	| undefined;

export type SlidesContextPayloadData =
	| {
			xml: string;
			contentId: string;
			title: string;
			url: string;
			selectedSlideIndex: number;
			selectedElementIds: string[];
			selectedTargets?: {
				targetId: string;
				owningElementId: string;
			}[];
			isViewMode?: boolean;
	  }
	| undefined;

export type DatabaseContextPayloadData =
	| {
			contentId: string;
			csv: string;
			title: string;
			url: string;
			selectedElementIds?: string[];
			isViewMode?: boolean;
	  }
	| undefined;

/** Partial database context for iframe updates (e.g. selection-only). */
export type DatabaseContextUpdatePayloadData = Partial<NonNullable<DatabaseContextPayloadData>>;

export type DatabaseContextPayload = PayloadCore<
	'database-context-payload',
	DatabaseContextUpdatePayloadData
>;

export type BrowserContextPayloadData = {
	context:
		| {
				browserUrl: string;
				htmlBody?: string;
				canvasText?: string;
				selection?: {
					content: string;
				};
		  }
		| undefined;
};

export type WorkflowContextPayloadData = {
	currentWorkflowDocument?: Record<string, unknown>;
};

export type JiraCreateContextPayloadData = {
	draftWorkItems:
		| {
				projectIdOrKey: string;
				issueTypeId: string;
				summary: string;
				fields: Record<string, unknown>;
		  }[]
		| null;
};

export type ArtifactContextPayloadData = {
	artifactId: string;
	mimeType: string;
	title: string;
	description: string;
	url: string;
};

// Not using PayloadCore because `data: type | undefined` is necessary
// but `| undefined` will cause `data` to be removed by PayloadCore.
export type ArtifactContextPayload = PayloadCore<'artifact-context-payload'> & {
	data: ArtifactContextPayloadData | undefined;
};

// Not using the PayloadCore because the `data: type | undefined` is necessary
// but `| undefined` will cause `data` to be removed by PayloadCore
export type EditorContextPayload = PayloadCore<'editor-context-payload'> & {
	data: EditorContextPayloadData;
};

// Not using the PayloadCore because the `data: type | undefined` is necessary
// but `| undefined` will cause `data` to be removed by PayloadCore
export type BrowserContextPayload = PayloadCore<'browser-context-payload'> & {
	data: BrowserContextPayloadData;
};

// Not using the PayloadCore because the `data: type | undefined` is necessary
// but `| undefined` will cause `data` to be removed by PayloadCore
export type WhiteboardContextPayload = PayloadCore<'whiteboard-context-payload'> & {
	data: WhiteboardContextPayloadData;
};

// Not using the PayloadCore because the `data: type | undefined` is necessary
// but `| undefined` will cause `data` to be removed by PayloadCore
export type JiraCreateContextPayload = PayloadCore<'jira-create-context-payload'> & {
	data: JiraCreateContextPayloadData;
};

export type ChatDraftPayload = PayloadCore<'chat-draft'>;

export type SmartLink3PProjectContext = {
	projectId: string;
	projectName: string;
	projectUrl: string;
};

export type SmartLink3PPostAuthProvider = 'Google Drive';

/**
 * Experiment-scoped Smart Link post-auth launch event for
 * platform_sl_3p_post_auth_chat_open_fg / platform_sl_3p_post_auth_chat_open_exp.
 *
 * This opens Rovo Chat in mini-modal mode with custom post-auth UI, without sending
 * a prompt. If the experiment does not become permanent, remove this event type as
 * part of the experiment cleanup.
 */
export type ChatSmartLink3PPostAuthLaunchPayload = PayloadCore<
	'chat-smartlink-3p-post-auth-launch',
	{
		extensionKey: string;
		provider: SmartLink3PPostAuthProvider;
		projectContext: SmartLink3PProjectContext;
	}
>;

export type OpenBrowseAgentPayload = PayloadCore<'open-browse-agent-modal'>;

export type OpenBrowseAgentSidebarPayload = PayloadCore<'open-browse-agent-sidebar'>;

/** Opens the skills list within the conversation that contains a rendered chat message. */
export type OpenBrowseSkillsPayload = PayloadCore<'open-browse-skills', { conversationId: string }>;

export type RovoRemixConsumer = 'editor' | 'renderer';

export type RovoRemixPreset = 'selection' | 'page';

export type RovoRemixOption =
	| 'chart'
	| 'diagram'
	| 'infographic'
	| 'visual-aid'
	| 'slides'
	| 'whiteboard'
	| 'database'
	| 'custom'
	| 'image';

export type OpenRovoRemixSidebarPayload = PayloadCore<
	'open-rovo-remix-sidebar',
	{
		remixConsumer: RovoRemixConsumer;
		remixPreset: RovoRemixPreset;
	}
>;

export type UpdateRovoRemixPresetPayload = PayloadCore<
	'update-rovo-remix-preset',
	{
		remixConsumer: RovoRemixConsumer;
		remixPreset: RovoRemixPreset;
	}
>;

export type SubmitRovoRemixPayload = PayloadCore<
	'submit-rovo-remix',
	{
		remixConsumer: RovoRemixConsumer;
		remixPreset: RovoRemixPreset;
		remixOption: RovoRemixOption;
		remixSubType?: string;
		userPrompt: string;
		conversationId?: string;
	}
> & { consumeOnce: true };

export type RovoRemixHandoffFailedPayload = PayloadCore<'rovo-remix-handoff-failed'>;

export type EditorSuggestionRichContent = {
	type: 'text/adf';
	version: 1;
	content: NonNullable<DocNode['content']>;
};

export type EditorSuggestionPayload = PayloadCore<
	'editor-suggestion',
	{
		mode: 'insert' | 'replace';
		content: string;
		agentId?: string;
		richContent?: EditorSuggestionRichContent;
	}
>;

export type UploadAndInsertMediaPayload = PayloadCore<
	'upload-and-insert-media',
	{
		sourceUrl: string;
	}
>;

export type EditorAgentChangedPayload = PayloadCore<
	'agent-changed',
	{
		agent: {
			id: string;
			name: string;
			identityAccountId?: string | null;
		} | null;
	}
>;

export type ChatOpenPayload = PayloadCore<
	'chat-open',
	{
		channelId: string;
		// @deprecated this is not being used, please use `chat-new` if you want to open a new chat with an agent
		agentId?: string;
		// Optimistic agent metadata for immediate header rendering - allows showing agent name
		// and avatar immediately from session data while full agent details are fetched in the background
		agentName?: string;
		agentIdentityAccountId?: string;
		avatarUrl?: string;
		// Reset to default chat view from previously selected view, e.g. Browse agents
		resetActiveMenu?: boolean;
		// Open the agent selector menu when chat is opened
		openAgentSelector?: boolean;
		// Optional AI feature context applied when the chat is opened
		aiFeatureContext?: Record<string, unknown>;
	}
>;

export type ForgeAppAuthSuccess = PayloadCore<
	'forge-auth-success',
	{
		is3pActionAuth?: boolean;
		agentMessageId?: string;
		authUrl?: string;
	}
>;
export type ForgeAppAuthFailure = PayloadCore<
	'forge-auth-failure',
	{
		is3pActionAuth?: boolean;
		errorMessage: string | undefined;
	}
>;

/** Inserts a prompt into the chat input - either:
 * - A prompt without a placeholder - sends as a message
 * - A prompt with a placeholder - inserts the prompt into the chat input
 */
export type InsertPromptPayload = PayloadCore<
	'insert-prompt',
	{
		prompt: string;
		/**
		 * Optional complete ADF document to seed the chat input with instead of the plain-string
		 * `prompt`. When provided, rich nodes (skill pills, links, inline cards, mentions) are
		 * preserved rather than being flattened to text. `prompt` is still used as a fallback.
		 */
		promptAdf?: DocNode;
		/**
		 * Overrides the default auto-send behavior for prompts.
		 * By default, prompts with backticks (`) are inserted as placeholders into the chat input
		 * (backticks indicate a placeholder), while prompts without backticks are sent immediately.
		 * Set this to true to insert prompts not containing backticks into the chat input for dynamic
		 * user completion, rather than sending them immediately.
		 */
		overrideAutoSend?: boolean;
		/**
		 * Optional conversation ID to target. When provided, the handler will resume
		 * the specified conversation (setting placeholder agent, opening the channel,
		 * and selecting the conversation) before inserting or sending the prompt.
		 */
		channelId?: string;
		agentId?: string;
		agentName?: string;
		agentIdentityAccountId?: string;
		avatarUrl?: string;
		/**
		 * Optional files to attach to the chat input alongside the prompt.
		 */
		files?: UploadedFile[];
	} & PlaceholderParam
>;

/** Inserts URLs as inline nodes into the chat input
 * - URLs are deduplicated against existing content
 * - Formatted as inline card nodes in the editor
 * - Supports multiple card types (Jira, Confluence, Trello, etc.)
 */
export type InsertUrlsPayload = PayloadCore<
	'insert-urls-into-prompt-input',
	{
		urls: string[];
	}
>;

/** Syncs a mounted chat input with its saved conversation draft. */
export type SyncPromptInputDraftPayload = PayloadCore<'sync-prompt-input-draft'>;

/** Inserts a skill chip into the chat input at the current cursor position. */
export type InsertSkillPayload = PayloadCore<
	'insert-skill-into-prompt-input',
	{
		skill: {
			id: string;
			name: string;
			slug: string;
			description?: string;
			product?: string;
			iconName?: string;
			color?: string;
		};
		skillSelectionSource?: string;
		targetPromptDraftAndModeKey?: string;
	}
>;

/** Opens the chat upload dialog so SKILL.md can be reviewed by Skill Builder. */
export type OpenSkillUploadPayload = PayloadCore<'open-skill-upload', void>;

/** Selects a conversation action by ID
 * - Used to programmatically open a specific action in the conversation actions list
 * - The action screen must be already open, and the actions list populated
 * - The action must exist in the current actions list
 */
export type SelectActionPayload = PayloadCore<
	'select-action',
	{
		actionId: string;
	}
>;

/**
 * Result shape when a chart is added to an AVP dashboard.
 * Matches the return type of ChartApiService.addChartToDashboard().
 */
export type AddChartToDashboardResult = {
	chart: Record<string, unknown> | null;
	canvasLayout: Record<string, unknown> | null;
};

/**
 * Fired when a chart is added to an AVP dashboard from a Rovo generated chart
 */
export type AddChartToDashboardPayload = PayloadCore<
	'add-chart-to-dashboard',
	AddChartToDashboardResult
>;

export type TransitionId = string;
export type StatusId = string;
export type StatusCategory = 'TODO' | 'IN_PROGRESS' | 'DONE' | 'UNDEFINED';
export type RuleConfig = {
	[key: string]: string;
};
export type AddStatusRovoPayload = {
	statusId: StatusId;
	statusName: string;
	statusCategory: StatusCategory;
	statusScope?: 'GLOBAL' | 'PROJECT' | null;
	isNewStatus?: boolean | null;
};
export type UpdateStatusRovoPayload = {
	statusId: StatusId;
	existingStatusName: string;
	newStatusName?: string | null;
	existingStatusCategory: StatusCategory;
	newStatusCategory?: StatusCategory | null;
	statusScope?: 'GLOBAL' | 'PROJECT' | null;
};
export type DeleteStatusRovoPayload = {
	statusId: string;
	statusName: string;
	statusCategory: StatusCategory;
};
export type AddNewTransitionRovoPayload = {
	id: TransitionId;
	name: string;
	toStatusId: StatusId;
	toStatusName: string;
	toStatusCategory: StatusCategory;
	links: {
		fromStatusId: StatusId;
		fromStatusName: string;
		fromStatusCategory: StatusCategory;
	}[];
};
export type UpdateTransitionRovoPayload = {
	id: TransitionId;
	name: string;
	toStatusId: StatusId;
	toStatusName: string;
	toStatusCategory: StatusCategory;
	links:
		| {
				fromStatusId: StatusId;
				fromStatusName: string;
				fromStatusCategory: StatusCategory;
		  }[]
		| null;
	existingName?: string | null;
	existingToStatusId?: StatusId | null;
	existingToStatusName?: string | null;
	existingToStatusCategory?: StatusCategory | null;
	existingLinks?:
		| {
				fromStatusId: StatusId;
				fromStatusName: string;
				fromStatusCategory: StatusCategory;
		  }[]
		| null;
};
export type DeleteTransitionRovoPayloadOld = {
	transitionId: TransitionId;
	transitionName: string;
};
export type DeleteTransitionRovoPayload = {
	id: TransitionId;
	name: string;
	toStatusId: StatusId;
	toStatusName: string;
	toStatusCategory: StatusCategory;
	links: {
		fromStatusId: StatusId;
		fromStatusName: string;
		fromStatusCategory: StatusCategory;
	}[];
};
export type AddRuleRovoPayload = {
	ruleTemplateKey: string;
	ruleConfig: RuleConfig;
	ruleDescription: string;
	transitionId: TransitionId;
};
export type UpdateRuleRovoPayload = {
	ruleTemplateKey: string;
	ruleConfig: RuleConfig;
	transitionId: TransitionId;
	ruleIdBeingEdited: string;
	ruleDescription: string;
};
export type DeleteRuleRovoPayload = {
	ruleId: string;
	ruleTemplateKey: string;
	ruleDescription: string;
	transitionId: TransitionId;
};
export type RedirectToWorkflowRovoPayload = {
	conversationId: string;
	url: string;
};

export type JiraWorkflowWizardAction =
	| { operationType: 'ADD_STATUS'; payload: AddStatusRovoPayload }
	| {
			operationType: 'UPDATE_STATUS';
			payload: UpdateStatusRovoPayload;
			isUsedInOtherWorkflows?: boolean;
	  }
	| { operationType: 'DELETE_STATUS'; payload: DeleteStatusRovoPayload }
	| { operationType: 'ADD_TRANSITION'; payload: AddNewTransitionRovoPayload }
	| { operationType: 'UPDATE_TRANSITION'; payload: UpdateTransitionRovoPayload }
	// TODO: Remove DeleteTransitionRovoPayloadOld when hix-7888_-_delete_transition_expanded_fields is cleaned up
	| {
			operationType: 'DELETE_TRANSITION';
			payload: DeleteTransitionRovoPayloadOld | DeleteTransitionRovoPayload;
	  }
	| { operationType: 'ADD_RULE'; payload: AddRuleRovoPayload }
	| { operationType: 'UPDATE_RULE'; payload: UpdateRuleRovoPayload }
	| { operationType: 'DELETE_RULE'; payload: DeleteRuleRovoPayload }
	| { operationType: 'REDIRECT_TO_WORKFLOW'; payload: RedirectToWorkflowRovoPayload };

export type JiraWorkflowWizardActionsPayload = PayloadCore<
	'jira-workflow-wizard-actions',
	{
		invocationId?: string;
		operations?: JiraWorkflowWizardAction[];
		currentWorkflowDocument?: Record<string, unknown>;
	}
>;

type MessageDescriptorWithValues = {
	// MessageDescriptor values
	id?: string;
	description?: string | object;
	defaultMessage?: string;

	// Message values to be passed to the message formatter
	messageValues?: Record<string, string | number | boolean | null | undefined | Date>;
};

export type GenericExternalActionErrorPayload = PayloadCore<
	'generic-external-action-error',
	{
		invocationId: string;
		errors: (string | MessageDescriptorWithValues)[];
	}
>;

export type DashboardInsightsActionsPayload = PayloadCore<'dashboard-insights-actions'> & {
	data?: DashboardInsightsActionsPayloadData;
};

export type DashboardInsightsActionsPayloadData =
	| {
			content: string;
	  }
	| undefined;

export type SetChatContextPayload = PayloadCore<'set-message-context', ChatContextPayload>;

export type OpenChatDebugModalPayload = PayloadCore<'open-chat-debug-modal'>;

export type OpenChatFeedbackModalPayload = PayloadCore<
	'open-chat-feedback-modal',
	{
		answerQuality: 'good' | 'bad' | 'general';
	}
>;

// Not using PayloadCore because `data: type | undefined` is necessary
// but `| undefined` will cause `data` to be removed by PayloadCore
export type SmartLinksContextPayload = PayloadCore<'smartlinks-context-payload'> & {
	/** Distinguishes unfinished discovery from a completed result with no links. */
	status?: 'loading' | 'resolved';
	/** Never opens chat — internal signal only. */
	openChat: false;
	data?: Array<{
		/**
		 * ORS auth key from meta.auth[0].key (e.g. 'gdrive', 'notion', 'miro').
		 * Matches the `serviceKey` query param in Knowledge API outboundAuthUrl,
		 * enabling the chat to cross-reference with Knowledge API without a mapping table.
		 */
		orsAuthKey: string;
		/**
		 * Human-readable provider name from ORS generator (e.g. 'Google Drive', 'Slack').
		 * Available regardless of SmartLinks auth state — matched against connector friendlyName.
		 */
		generatorName?: string;
		/** Representative page URL for filling a contextual third-party starter. */
		representativeUrl?: string;
		/** Number of occurrences on the page — used for ranking in the banner. */
		count: number;
	}>;
};

/** Published by the consumer hook to notify the publisher whether it should fetch SmartLinks. */
export type SpaceSelectedPayload = PayloadCore<
	'space-selected',
	{
		spaceId: string;
		title: string;
		emoji: string;
		description?: string;
		entryPoint?: RovoSpaceEntryPoint;
	}
>;

export type SpaceDeselectedPayload = PayloadCore<'space-deselected'>;

/**
 * Published by the Rovo chat custom-skill update action once the user confirms an
 * `UpdateCustomSkillTool` action. Carries the confirmed skill content so the open
 * Studio custom-skill view/edit page (matched by `skillAri`) can reflect the change
 * optimistically in the Relay store without refetching.
 */
export type CustomSkillUpdatePayload = PayloadCore<
	'custom-skill-update',
	{
		skillAri: string;
		name: string;
		displayName: string;
		/**
		 * Concise, user-facing summary of the skill's purpose. Optional because not every
		 * publisher edits it — subscribers should fall back to their own persisted value.
		 */
		helpText?: string;
		description: string;
		instructions: string;
		tools: { id: string; source: string; type: string }[];
	}
>;

/**
 * Published by the Rovo chat custom-skill create action once a newly created skill has been
 * finished successfully. The skill is not in any cached list yet, so subscribers such as the Studio
 * custom skills page refetch their list.
 */
export type CustomSkillCreatePayload = PayloadCore<'custom-skill-create'>;

/**
 * Published once a Rovo agent draft has been created (e.g. by Smart Create or the inline
 * create-agent card). Lets hosts such as the Studio Agents landing page add the draft to
 * their cached lists without refetching.
 */
export type RovoAgentDraftCreatedPayload = PayloadCore<
	'rovo-agent-draft-created',
	{ agentAri: string; cloudId: string }
>;

/**
 * Published once a Rovo agent has been published from a Rovo surface (e.g. the Smart Create
 * modal or the inline create-agent card). Lets hosts such as the Studio Agents landing page
 * move the agent into their published lists without refetching.
 */
export type RovoAgentPublishedPayload = PayloadCore<
	'rovo-agent-published',
	{ agentAri: string; cloudId: string }
>;

export const CUSTOM_SKILL_DELETED_EVENT = 'custom-skill-deleted' as const;

/** Published only after a custom skill has been successfully deleted. */
export type CustomSkillDeletedPayload = PayloadCore<
	typeof CUSTOM_SKILL_DELETED_EVENT,
	{ cloudId: string; skillAri: string }
> & { openChat: false };

export type RecommendedSpacesSelectedPayload = PayloadCore<'recommended-spaces-selected'>;
export type RecommendedSpacesFirstTimeSelectedPayload =
	PayloadCore<'recommended-spaces-first-time-selected'>;

export type SmartlinksSubscriptionChangedPayload =
	PayloadCore<'smartlinks-subscription-changed'> & {
		/** Never opens chat — internal signal only. */
		openChat: false;
		isActive: boolean;
	};

/**
 * Requests that the currently mounted in-context AIFC modal close.
 *
 * This event is intentionally unscoped because only one such modal
 * is expected to be open at a time.
 */
export type CloseInContextAifcModalPayload = PayloadCore<'close-in-context-aifc-modal'>;

export type AgentEditorOpenToolsPayload = PayloadCore<
	'agent-editor-open-tools',
	{ agentId: string; cloudId: string }
> & { openChat: false };

export type Payload =
	| AgentEditorOpenToolsPayload
	| MessageSendPayload
	| ChatClosePayload
	| CloseInContextAifcModalPayload
	| SmartCreationModalOpenPayload
	| ChatNewPayload
	| ChatDraftPayload
	| ChatSmartLink3PPostAuthLaunchPayload
	| EditorContextPayload
	| ChatOpenPayload
	| OpenBrowseAgentPayload
	| SmartlinksSubscriptionChangedPayload
	| OpenBrowseAgentSidebarPayload
	| OpenBrowseSkillsPayload
	| OpenRovoRemixSidebarPayload
	| UpdateRovoRemixPresetPayload
	| SubmitRovoRemixPayload
	| RovoRemixHandoffFailedPayload
	| EditorSuggestionPayload
	| EditorAgentChangedPayload
	| BrowserContextPayload
	| WhiteboardContextPayload
	| JiraCreateContextPayload
	| ArtifactContextPayload
	| JiraInlineAgentCreationAgentAssignedPayload
	| JiraWorkItemsCreatingPayload
	| JiraWorkItemsCreatedPayload
	| JiraWorkItemsCreateFailedPayload
	| DatabaseContextPayload
	| ForgeAppAuthSuccess
	| ForgeAppAuthFailure
	| JiraWorkflowWizardActionsPayload
	| InsertPromptPayload
	| DashboardInsightsActionsPayload
	| SetChatContextPayload
	| InsertUrlsPayload
	| InsertSkillPayload
	| OpenSkillUploadPayload
	| SyncPromptInputDraftPayload
	| SelectActionPayload
	| AddChartToDashboardPayload
	| GenericExternalActionErrorPayload
	| OpenChatDebugModalPayload
	| OpenChatFeedbackModalPayload
	| JsmJourneyBuilderActionsPayload
	| StudioAutomationBuildUpdatePayload
	| SolutionArchitectHandoffPayload
	| SolutionPlanStateUpdatePayload
	| SolutionDraftAgentUpdatePayload
	| SolutionArchitectAgentActivationPayload
	| SolutionArchitectAgentActivationFlowStartedPayload
	| SolutionArchitectAgentActivationFlowStoppedPayload
	| UpdateAgentConfigurationPayload
	| StudioLandingPageRedirectPayload
	| UploadAndInsertMediaPayload
	| SmartLinksContextPayload
	| SpaceSelectedPayload
	| SpaceDeselectedPayload
	| RecommendedSpacesSelectedPayload
	| RecommendedSpacesFirstTimeSelectedPayload
	| CustomSkillUpdatePayload
	| CustomSkillCreatePayload
	| RovoAgentDraftCreatedPayload
	| RovoAgentPublishedPayload
	| CustomSkillDeletedPayload
	| TaskPlanConfirmedPayload
	| TaskAskQuestionRenderedPayload
	| TaskPlanRenderedPayload
	| TaskSkipAllQuestionsPayload
	| TaskCancelPlanPayload
	| TaskAskQuestionConfirmedPayload
	| TaskModifyPlanRequestedPayload
	| TaskModifyPlanSubmittedPayload
	| ConfluenceContentFinalizedPayload;

export type TaskPlanConfirmedPayload = PayloadCore<
	'task-plan-confirmed',
	{ conversationId: string; planTitle: string }
>;

export type TaskAskQuestionRenderedPayload = PayloadCore<
	'task-ask-question-rendered',
	{ conversationId: string; invocationId: string }
>;

export type TaskPlanRenderedPayload = PayloadCore<
	'task-plan-rendered',
	{ conversationId: string; invocationId: string }
>;

export type TaskSkipAllQuestionsPayload = PayloadCore<
	'task-skip-all-questions',
	{ conversationId: string; invocationId: string }
>;

export type TaskAskQuestionConfirmedPayload = PayloadCore<
	'task-ask-question-confirmed',
	{ conversationId: string; invocationId: string }
>;

export type TaskCancelPlanPayload = PayloadCore<
	'task-cancel-plan',
	{ conversationId: string; invocationId: string }
>;

export type TaskModifyPlanRequestedPayload = PayloadCore<
	'task-modify-plan-requested',
	{ conversationId: string; invocationId: string }
>;

export type TaskModifyPlanSubmittedPayload = PayloadCore<
	'task-modify-plan-submitted',
	{ conversationId: string; invocationId: string; prompt: DocNode }
>;

export const JIRA_INLINE_AGENT_CREATION_AGENT_ASSIGNED_EVENT =
	'jira-inline-agent-creation-agent-assigned' as const;

export type JiraInlineAgentCreationAgentAssignedPayload = PayloadCore<
	typeof JIRA_INLINE_AGENT_CREATION_AGENT_ASSIGNED_EVENT,
	{ issueId: string }
>;

/**
 * Published right before the `create-work-items` skill calls the bulk-create API, carrying the draft
 * work items the user submitted. Subscribers (e.g. the Jira list view) can use the draft fields to
 * render optimistic rows immediately, ahead of the (potentially slow) create request. Correlate this
 * batch with the follow-up {@link JIRA_WORK_ITEMS_CREATED_EVENT} / {@link JIRA_WORK_ITEMS_CREATE_FAILED_EVENT}
 * via `invocationId`.
 */
export const JIRA_WORK_ITEMS_CREATING_EVENT = 'jira-work-items-creating' as const;

export type JiraWorkItemCreatingDraft = {
	/** Skill invocation id of this draft; used as the optimistic row's stable id. */
	invocationId: string;
	/** Draft summary, used to render the optimistic row immediately. */
	summary: string;
	/** Optional Issue type of this draft, used to render the optimistic row's issue-type icon immediately. */
	issueType?: {
		id: string;
		name: string;
		iconUrl: string;
	};
};

export type JiraWorkItemsCreatingPayload = PayloadCore<
	typeof JIRA_WORK_ITEMS_CREATING_EVENT,
	{
		/**
		 * Identifies this batch across the creating/created/failed lifecycle events. Conceptually a
		 * per-batch id, distinct from the per-suggestion {@link JiraWorkItemCreatingDraft.invocationId}
		 * ids; on the FE it is derived from the first draft's invocation id (unique per submission,
		 * stable across the three events).
		 */
		invocationId: string;
		/** Draft work items submitted for creation (parents and their children, flattened). */
		draftWorkItems: JiraWorkItemCreatingDraft[];
	}
>;

export const JIRA_WORK_ITEMS_CREATED_EVENT = 'jira-work-items-created' as const;

export type JiraWorkItemsCreatedPayload = PayloadCore<
	typeof JIRA_WORK_ITEMS_CREATED_EVENT,
	{
		/** Identifies the batch; matches the {@link JIRA_WORK_ITEMS_CREATING_EVENT} `invocationId`. */
		invocationId: string;
		/** Ids of the work items created by the skill, used by subscribers to fetch their data. */
		createdIssueIds: string[];
	}
>;

/**
 * Published when the `create-work-items` skill fails to create the work items it announced via a
 * {@link JIRA_WORK_ITEMS_CREATING_EVENT}. Subscribers use `invocationId` to revert any optimistic
 * rows they rendered for that batch.
 */
export const JIRA_WORK_ITEMS_CREATE_FAILED_EVENT = 'jira-work-items-create-failed' as const;

export type JiraWorkItemsCreateFailedPayload = PayloadCore<
	typeof JIRA_WORK_ITEMS_CREATE_FAILED_EVENT,
	{
		/** Identifies the batch; matches the {@link JIRA_WORK_ITEMS_CREATING_EVENT} `invocationId` to revert its optimistic rows. */
		invocationId: string;
	}
>;

/**
 * Published when staged Confluence content has been published
 * out of the Rovo system space into a real space. Any surface showing that content (e.g. a mounted
 * Rovo preview card) subscribes and refreshes its own state
 */
export const CONFLUENCE_CONTENT_FINALIZED_EVENT = 'confluence-content-finalized' as const;

export type ConfluenceContentFinalizedPayload = PayloadCore<
	typeof CONFLUENCE_CONTENT_FINALIZED_EVENT,
	{
		/**
		 * Every content id the finalize attempt covered, successful or not. A failed item's content
		 * just stays in the Rovo system space, so a subscriber's refetch harmlessly reconfirms that.
		 * Content ids are unique across sites, so subscribers match on this alone.
		 */
		contentIds: string[];
		/** The conversation that produced the finalized content. */
		conversationId?: string;
		/**
		 * The final destination returned after Convo AI resolves a finalized page.
		 * Present when the destination-ready finalized-content gate is enabled.
		 */
		finalizedDestination?: { baseUrl: string; path: string };
	}
> & {
	/** Never opens chat — internal signal only. */
	openChat: false;
};

export type Callback = (payload: Payload) => void;

export type TopicEvents = {
	[key in Topic]?: Array<{
		id: string;
		callback: Callback;
	}>;
};

export type TopicEventQueue = {
	[key in Topic]?: Payload;
};

export type UploadedFile = {
	id: string;
	conversation_channel_id?: string;
	blob_id?: string;
	name: string;
	size?: number;
	type?: string;
	created_at?: string;
	owner?: string;
	isLoading: boolean;
	error?: string;
	fileObject?: File;
};

type SendMessageSearchArtifact = {
	/** The Atlassian Resource Identifier (ARI) of the object. */
	ari?: string;
	/** The URL of the object. */
	url?: string;
};
